import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy, signal, computed, OnChanges, SimpleChanges } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

@Component({
  selector: 'app-document-viewer',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './document-viewer.component.html',
  styleUrls: ['./document-viewer.component.scss'],
})
export class DocumentViewerComponent implements OnChanges {
  @Input() document: any;
  @Input() fileUrl: string = '';
  @Input() isLoading = false;
  @Input() error: string | null = null;

  @Output() onClose = new EventEmitter<void>();
  @Output() onDownload = new EventEmitter<void>();

  pdfLoading = signal(true);
  isLoadingDownloadDocument = signal(false);
  downloadError = signal(false);
  errorMessage = signal<string | null>(null);
  blobUrl = signal<string>('');

  constructor(private sanitizer: DomSanitizer) {}

  sanitizedFileUrl = computed(() => {
    const url = this.blobUrl() || this.fileUrl;
    return url ? this.sanitizer.bypassSecurityTrustResourceUrl(url) : null;
  });

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['fileUrl'] && this.fileUrl && this.isPdf) {
      this.loadPdfAsBlob();
    }
  }

  private loadPdfAsBlob(): void {
    this.pdfLoading.set(true);
    fetch(this.fileUrl)
      .then(response => response.blob())
      .then(blob => {
        const url = URL.createObjectURL(blob);
        this.blobUrl.set(url);
        this.pdfLoading.set(false);
      })
      .catch(error => {
        console.error('Error loading PDF:', error);
        this.pdfLoading.set(false);
      });
  }

  get fileName(): string {
    return this.document?.file_name || 'Document';
  }

  get fileExtension(): string {
    if (!this.document?.file_name) return '';
    return this.document.file_name.split('.').pop()?.toLowerCase() || '';
  }

  get isPdf(): boolean {
    return this.fileExtension === 'pdf';
  }

  get isImage(): boolean {
    return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(this.fileExtension);
  }

  get isOfficeDocument(): boolean {
    return ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'].includes(this.fileExtension);
  }

  closeDialog(): void {
    this.onClose.emit();
  }

  downloadDocument(): void {
    this.isLoadingDownloadDocument.set(true);
    this.onDownload.emit();
    // Error handling should be done in parent component
    setTimeout(() => {
      this.isLoadingDownloadDocument.set(false);
    }, 1000);
  }

  onPdfLoad(): void {
    this.pdfLoading.set(false);
  }

  onPdfError(): void {
    this.pdfLoading.set(false);
    this.errorMessage.set('Impossible de charger le PDF. Veuillez télécharger le fichier.');
  }

  onImageError(): void {
    this.errorMessage.set('Impossible de charger l\'image. Veuillez télécharger le fichier.');
  }

  isKnownOfficeExtension(): boolean {
    return ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'].includes(this.fileExtension);
  }
}
